import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/use-toast";
import { SetStateAction, useEffect, useState } from "react";
import {
  FormProvider,
  useForm,
  Control,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import BookingAddOn from "./add-on";
import BookingAvailability from "./availability-form";
import GeneralInfoForm from "./general-info-form";
import PricingAndRefund from "./pricing-and-refund";
import { Button } from "@/components/ui/button";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import { LoadingButton } from "@/components/ui/common/loading-button";
import {
  FacilitySchedule as FacilityScheduleTypes,
  ScheduleType,
} from "@/types/facility/schedule";
import {
  useCreateFacilityScheduleMutation,
  useUpdateFacilityScheduleMutation,
} from "@/services/facilityApis";
import { format } from "date-fns";
import { ErrorType, FormProps } from "@/types";
import { cn } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

// Define the shared form props interface for all child components
interface FormStepProps {
  control: Control<FacilityScheduleTypes>;
  errors: FieldErrors<FacilityScheduleTypes>;
  watch: UseFormWatch<FacilityScheduleTypes>;
  setValue: UseFormSetValue<FacilityScheduleTypes>;
  trigger?: UseFormTrigger<FacilityScheduleTypes>;
  action: "add" | "edit";
  open: boolean;
}

const stepContentComponents: React.ComponentType<FormStepProps>[] = [
  GeneralInfoForm,
  BookingAvailability,
  BookingAddOn,
  PricingAndRefund,
];

const getStepContent = (step: number, formProps: FormStepProps) => {
  const Component = stepContentComponents[step - 1];
  return Component ? (
    <Component {...formProps} action={formProps.action} open={formProps.open} />
  ) : null;
};

const steps = [
  { label: "General Information", key: 1 },
  { label: "Availability & Schedule", key: 2 },
  { label: "Add Ons", key: 3 },
  { label: "Pricing & Refund", key: 4 },
];

const FacilityScheduleForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  data,
  setData,
  refetch,
}: FormProps<FacilityScheduleTypes>) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const [activeStep, setActiveStep] = useState(1);
  const [hasAvailabilityTimeErrors, setHasAvailabilityTimeErrors] =
    useState(false);
  const [hasOverlapErrors, setHasOverlapErrors] = useState(false);

  // Add default values for all form fields
  const initialValues: FacilityScheduleTypes = {
    org_id: orgId,
    general_info: {
      facility_id: 0,
      type: ScheduleType.FacilitySchedule,
      color: "",
      max_attendees: undefined,
      break_duration: undefined,
      income_category_id: undefined,
      tax_group_id: undefined,
      advance_booking_days: undefined,
      start_date: format(new Date(), "yyyy-MM-dd"), // YYYY-MM-DD format
      always_available: false,
      is_exclusive: false,
      member_only: false,
      price: 0,
      duration_per_slot: 0,
      availability: {
        sunday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        monday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        tuesday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        wednesday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        thursday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        friday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        saturday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
      },
      location: "",
      description: "",
      end_date: undefined,
    },
    tier_pricing: [],
    plan_pricing: [],
    group_pricing: [],
    special_pricing: [],
    refund_policies: [], // Default refund policy
    add_ons: [],
  };

  const [createSchedule, { isLoading: isCreating }] =
    useCreateFacilityScheduleMutation();
  const [updateSchedule, { isLoading: isUpdating }] =
    useUpdateFacilityScheduleMutation();

  const form = useForm<FacilityScheduleTypes>({
    mode: "onChange", // Change to onChange for real-time validation
    reValidateMode: "onChange", // Re-validate on change
    defaultValues: initialValues,
    // Remove custom resolver to let Controllers handle their own validation
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    trigger,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();
  useEffect(() => {
    if (action == "edit" && data) {
      const payload = data as FacilityScheduleTypes;
      const normalized: FacilityScheduleTypes = {
        ...payload,
        tier_pricing: Array.isArray(payload.tier_pricing)
          ? payload.tier_pricing
          : [],
        plan_pricing: Array.isArray(payload.plan_pricing)
          ? payload.plan_pricing.map((p: any) => ({
            group_id: p?.group_id ? Number(p.group_id) : 0,
            membership_ids: Array.isArray(p?.membership_ids)
              ? p.membership_ids.map(Number)
              : [],
            price: Number(p?.price) || 0,
          }))
          : [],
        special_pricing: Array.isArray(payload.special_pricing)
          ? payload.special_pricing
          : [],
        refund_policies: Array.isArray(payload.refund_policies)
          ? payload.refund_policies
          : [],
        add_ons: Array.isArray(payload.add_ons) ? payload.add_ons : [],
        general_info: {
          ...payload.general_info,
          price: Number(payload.general_info?.price) || 0,
          duration_per_slot:
            Number(payload.general_info?.duration_per_slot) || 0,
        },
      };
      reset(normalized);
    } else if (action == "add") {
      reset(initialValues);
    }
  }, [action, data, reset]);

  useEffect(() => {
    const handleAvailabilityTimeErrors = (event: any) => {
      setHasAvailabilityTimeErrors(event.detail.hasErrors);
    };
    const handleOverlapStatus = (event: any) => {
      setHasOverlapErrors(event.detail.hasOverlap);
    };

    document.addEventListener(
      "availability-time-errors",
      handleAvailabilityTimeErrors as any,
    );
    document.addEventListener(
      "overlap-status-changed",
      handleOverlapStatus as any,
    );

    return () => {
      document.removeEventListener(
        "availability-time-errors",
        handleAvailabilityTimeErrors as any,
      );
      document.removeEventListener(
        "overlap-status-changed",
        handleOverlapStatus as any,
      );
    };
  }, []);

  // Update onSubmit function to handle the entire form data
  const onSubmit = async (data: FacilityScheduleTypes) => {
    try {
      const {
        general_info,
        tier_pricing,
        plan_pricing,
        group_pricing,
        special_pricing,
        refund_policies,
        // Exclude validation fields from API payload
        slot_overlaps,
        ...restData
      } = data;

      // Create the final payload with proper structure
      const payload: FacilityScheduleTypes = {
        org_id: orgId,
        // Keep general_info as a nested object - this is what the API expects
        general_info: {
          ...general_info,
          // Ensure correct field names
          is_exclusive: general_info.is_exclusive || false,
          // Dates are already in YYYY-MM-DD string format
          start_date: general_info.start_date,
          end_date: general_info.end_date,
        },

        // Pricing arrays at root level
        tier_pricing: tier_pricing?.length > 0 ? tier_pricing : [],
        plan_pricing:
          plan_pricing && plan_pricing.length > 0 ? plan_pricing : [],
        special_pricing:
          special_pricing?.length > 0
            ? special_pricing.map((sp) => ({
              ...sp,
              // Dates are already strings in YYYY-MM-DD format
              date_from: sp.date_from,
              date_to: sp.date_to,
            }))
            : [],

        // Refund policies should always be included (API requires at least one)
        refund_policies: refund_policies?.length > 0 ? refund_policies : [],
        group_pricing:
          group_pricing && group_pricing.length > 0 ? group_pricing : [],
        add_ons: restData.add_ons || [],
      };

      // Call your API to save the data
      let response;
      if (action == "add") {
        response = await createSchedule(payload).unwrap();
      } else {
        // For update, include the ID in the general_info
        const updatePayload = {
          ...payload,
          general_info: {
            ...payload.general_info,
            id: general_info.id,
          },
        };
        response = await updateSchedule(updatePayload).unwrap();
      }

      toast({
        variant: "success",
        title:
          action == "add"
            ? "Facility Schedule created successfully"
            : "Facility Schedule updated successfully",
      });

      // Close the form and refresh data
      handleClose();
      refetch();
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: `Something Went Wrong.`,
          description: `Failed to save facility booking schedule`,
        });
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    setData(undefined);
    setActiveStep(1); // Reset to first step
    reset(initialValues, {
      keepIsSubmitted: false, // Reset the form's submission state
      keepSubmitCount: false, // Reset the count of submissions
      keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
      keepDefaultValues: true, // Keep default values as provided by initialValue
    });

    setOpen(false);
  };

  const handleNext = async () => {
    if (activeStep < steps.length) {
      // First step validation
      if (activeStep === 1) {
        const { general_info } = getValues();

        // Create validation triggers for all required fields
        const fieldsToValidate = [
          "general_info.facility_id",
          "general_info.color",
          "general_info.income_category_id",
          "general_info.max_attendees",
          "general_info.advance_booking_days",
          "general_info.start_date",
          "general_info.end_date",
        ];

        // Trigger validation for all fields
        form
          .trigger(fieldsToValidate as (keyof FacilityScheduleTypes)[])
          .then((isValid) => {
            if (!isValid) {
              return;
            }

            // Date validation
            if (general_info.start_date && general_info.end_date) {
              const start = new Date(general_info.start_date);
              const end = new Date(general_info.end_date);

              if (end <= start) {
                toast({
                  variant: "destructive",
                  description: "End date must be after start date",
                });
                return;
              }
            }

            // If all validations pass, move to next step
            setActiveStep(activeStep + 1);
          });
      }
      // Second step validation (Availability & Schedule)
      else if (activeStep === 2) {
        const isStepValid = await trigger();

        // Check for slot overlaps only if we are on the availability step (step 2)
        if (activeStep === 2 && hasOverlapErrors) {
          toast({
            variant: "destructive",
            title: "Schedule Conflict",
            description:
              "This schedule timing overlaps with another event/booking. Please adjust your availability settings to resolve conflicts.",
          });
          return;
        }

        // Check for time validation errors from availability form
        if (activeStep === 2 && hasAvailabilityTimeErrors) {
          // Find the first error message to display from `form.formState.errors`
          const timeErrors = errors.general_info?.availability as any;
          let errorMessage =
            "Please fix time validation errors before proceeding.";
          if (timeErrors) {
            for (const dayKey of Object.keys(timeErrors)) {
              const dayErrors = timeErrors[dayKey];
              if (dayErrors.start_time) {
                errorMessage = dayErrors.start_time.message || errorMessage;
                break;
              }
              if (dayErrors.end_time) {
                errorMessage = dayErrors.end_time.message || errorMessage;
                break;
              }
            }
          }

          toast({
            variant: "destructive",
            title: "Time Validation Error",
            description: errorMessage,
          });
          return;
        }

        // Check if at least one day is selected with valid times when on step 2
        if (activeStep === 2) {
          // First, check if duration is filled (required field)
          const { general_info } = getValues();

          if (
            !general_info.duration_per_slot ||
            general_info.duration_per_slot <= 0
          ) {
            toast({
              variant: "destructive",
              title: "Duration Required",
              description:
                "Please enter a duration per slot before proceeding.",
            });
            return;
          }

          // Trigger form validation for duration_per_slot field
          const isDurationValid = await form.trigger(
            "general_info.duration_per_slot",
          );
          if (!isDurationValid) {
            toast({
              variant: "destructive",
              title: "Duration Validation Error",
              description:
                "Please enter a valid duration per slot before proceeding.",
            });
            return;
          }

          // No longer need this block as we are using hasAvailabilityTimeErrors state from event listener
          // let hasTimeErrors = false;
          // let timeErrorDetails: { errors: any; hasErrors: boolean } | null = null;
          // const checkTimeErrors = () => {
          //   return new Promise<{ errors: any; hasErrors: boolean }>((resolve) => {
          //     const handler = (event: any) => {
          //       resolve(event.detail);
          //       document.removeEventListener("availability-time-errors", handler);
          //     };
          //     document.addEventListener("availability-time-errors", handler);
          //     setTimeout(() => {
          //       document.removeEventListener("availability-time-errors", handler);
          //       resolve({ errors: {}, hasErrors: false });
          //     }, 100);
          //   });
          // };
          // timeErrorDetails = await checkTimeErrors();
          // hasTimeErrors = timeErrorDetails.hasErrors;

          // Check if at least one day is selected with valid times
          const availability = getValues("general_info.availability");
          if (availability) {
            const hasValidDay = Object.values(availability).some(
              (day: any) =>
                day?.is_available &&
                day?.time_slots?.start_time &&
                day?.time_slots?.end_time,
            );

            if (!hasValidDay) {
              toast({
                variant: "destructive",
                title: "Availability Required",
                description:
                  "Please select at least one day and set its start and end times before proceeding.",
              });
              return;
            }
          } else {
            toast({
              variant: "destructive",
              title: "Availability Required",
              description:
                "Please select at least one day and set its start and end times before proceeding.",
            });
            return;
          }
        }

        // Check for date validation errors
        if (errors.general_info?.start_date || errors.general_info?.end_date) {
          const startDateError = errors.general_info?.start_date?.message;
          const endDateError = errors.general_info?.end_date?.message;

          if (startDateError || endDateError) {
            toast({
              variant: "destructive",
              title: "Date Validation Error",
              description:
                startDateError ||
                endDateError ||
                "Please correct the date fields before proceeding.",
            });
            return;
          }
        }

        if (isStepValid && !hasOverlapErrors && activeStep < steps.length) {
          setActiveStep(activeStep + 1);
        } else if (hasOverlapErrors) {
          toast({
            variant: "destructive",
            title: "Schedule Conflict",
            description:
              "This schedule timing overlaps with another event/booking. Please adjust your availability settings to resolve conflicts.",
          });
        }
      }
      // Add validation for Add-ons
      else if (activeStep === 3) {
        const { general_info, add_ons } = getValues();

        // Check if facility is selected
        if (!general_info.facility_id) {
          toast({
            variant: "destructive",
            description: "Please select a facility first.",
          });
          return;
        }

        // if (add_ons && add_ons.length > 0) {
        //   let hasValidationError = false;
        //   for (const addOn of add_ons) {
        //     if (!addOn.max_attendees || addOn.max_attendees < 1) {
        //       toast({
        //         variant: "destructive",
        //         description:
        //           "Please enter valid max attendees (minimum 1) for all selected add-ons.",
        //       });
        //       hasValidationError = true;
        //       break;
        //     }
        //   }
        //   if (hasValidationError) {
        //     return;
        //   }
        // }

        setActiveStep(activeStep + 1);
      }
      // Pricing & Refund validation
      else if (activeStep === steps.length) {
        const {
          general_info,
          tier_pricing: tierPricingEnabled,
          plan_pricing: planBasedPricing,
          special_pricing: specialPricing,
          refund_policies: refunds,
        } = getValues();

        // regularPricing, tierPricingEnabled, tiers, planBasedPricing, plans, specialPricing, specialPricingRules, refunds } = getValues();

        // Check regular pricing
        if (!general_info.price || general_info.price <= 0) {
          toast({
            variant: "destructive",
            description: "Regular pricing must be greater than zero.",
          });
          return;
        }

        // If tier pricing is enabled, validate tier fields
        if (tierPricingEnabled && tierPricingEnabled.length > 0) {
          // Check if all tiers are properly filled and within limits
          const invalidTier = tierPricingEnabled.find((tier) => {
            // Check each specific condition and provide a detailed error
            if (!tier.quantity || tier.quantity <= 0) {
              toast({
                variant: "destructive",
                description: "Quantity must be between 1-1000 for all tiers.",
              });
              return true;
            }

            if (tier.quantity > 1000) {
              toast({
                variant: "destructive",
                description: "Quantity cannot exceed 1000 for any tier.",
              });
              return true;
            }

            if (!tier.price || tier.price <= 0) {
              toast({
                variant: "destructive",
                description: "Price must be greater than zero for all tiers.",
              });
              return true;
            }

            return false;
          });

          if (invalidTier) {
            return;
          }

          // Check if the total quantity across all tiers exceeds 1000
          const totalQuantity = tierPricingEnabled.reduce(
            (sum, tier) => sum + (tier.quantity || 0),
            0,
          );
          if (totalQuantity > 1000) {
            toast({
              variant: "destructive",
              description:
                "Total number of events across all tiers cannot exceed 1000.",
            });
            return;
          }

          // Check for duplicates in event numbers
          const eventCounts = tierPricingEnabled.map((tier) => tier.quantity);
          const uniqueCounts = new Set(eventCounts);
          if (uniqueCounts.size !== eventCounts.length) {
            toast({
              variant: "destructive",
              description:
                "Duplicate event counts are not allowed in tier pricing.",
            });
            return;
          }

          // Check for duplicate tier quantities
          const quantities = tierPricingEnabled.map((tier) => tier.quantity);
          const uniqueQuantities = new Set(quantities);
          if (uniqueQuantities.size !== quantities.length) {
            toast({
              variant: "destructive",
              description:
                "Duplicate tier quantity detected. Please use unique quantities",
            });
            return;
          }
        }

        // If plan based pricing is enabled, validate plan fields
        if (planBasedPricing && planBasedPricing.length > 0) {
          // Check if all plans are properly filled and no duplicates
          const allMembershipPlanIds = new Set();
          let hasDuplicate = false;
          let hasInvalidGroup = false;
          let hasNoMemberships = false;
          let hasInvalidPrice = false;

          for (const plan of planBasedPricing) {
            if (!plan.group_id || plan.group_id === 0) {
              hasInvalidGroup = true;
              break;
            }

            if (!plan.membership_ids || plan.membership_ids.length === 0) {
              hasNoMemberships = true;
              break;
            }

            // Check if price is NOT greater than zero (must be positive)
            if (plan.price <= 0) {
              hasInvalidPrice = true;
              break;
            }

            for (const mId of plan.membership_ids) {
              if (allMembershipPlanIds.has(mId)) {
                hasDuplicate = true;
                break;
              }
              allMembershipPlanIds.add(mId);
            }
            if (hasDuplicate) break;
          }

          if (hasInvalidGroup) {
            toast({
              variant: "destructive",
              description:
                "All plan entries must have a membership group selected.",
            });
            return;
          }

          if (hasNoMemberships) {
            toast({
              variant: "destructive",
              description:
                "All plan entries must have at least one membership plan selected.",
            });
            return;
          }

          if (hasInvalidPrice) {
            toast({
              variant: "destructive",
              description:
                "Price must be greater than zero for all membership plans.",
            });
            return;
          }

          if (hasDuplicate) {
            toast({
              variant: "destructive",
              description: "Duplicate membership plans are not allowed.",
            });
            return;
          }
        }

        // If special pricing is enabled, validate special pricing fields
        if (specialPricing) {
          // Check if at least one rule exists
          if (!specialPricing || specialPricing.length === 0) {
            toast({
              variant: "destructive",
              description:
                "At least one rule must be added when special pricing is enabled.",
            });
            return;
          }

          // Check if all rules are properly filled
          for (const rule of specialPricing) {
            if (!rule.name || rule.name.trim() === "") {
              toast({
                variant: "destructive",
                description: "All special pricing rules must have a name.",
              });
              return;
            }

            if (rule.price < 0) {
              toast({
                variant: "destructive",
                description:
                  "All special pricing rules must have a valid positive price.",
              });
              return;
            }

            if (!rule.date_from) {
              toast({
                variant: "destructive",
                description:
                  "All special pricing rules must have a start date.",
              });
              return;
            }

            if (!rule.date_to) {
              toast({
                variant: "destructive",
                description: "All special pricing rules must have an end date.",
              });
              return;
            }

            // Check if end date is after start date
            if (new Date(rule.date_to) <= new Date(rule.date_from)) {
              toast({
                variant: "destructive",
                description: `End date must be after start date for rule: ${rule.name}`,
              });
              return;
            }
          }
        }

        // Validate the refund fields
        if (!refunds || refunds.length === 0) {
          toast({
            variant: "destructive",
            description: "At least one refund rule must be defined.",
          });
          return;
        }

        // Check if maximum 7 refund rules
        if (refunds.length > 7) {
          toast({
            variant: "destructive",
            description: "Maximum 7 refund rules are allowed.",
          });
          return;
        }

        // Check for duplicates in refund periods and empty values
        const refundPeriods = new Set();
        let hasInvalidRefund = false;
        let hasDuplicateRefund = false;

        for (const refund of refunds) {
          // Empty period check
          if (!refund || refund.cancellation_period === "") {
            hasInvalidRefund = true;
            break;
          }

          if (refundPeriods.has(refund.cancellation_period)) {
            hasDuplicateRefund = true;
            break;
          }

          refundPeriods.add(refund.cancellation_period);

          // Check if refund percentage is valid (greater than 0 and <= 100)
          if (refund.refund_percentage <= 0 || refund.refund_percentage > 100) {
            toast({
              variant: "destructive",
              description: "Refund percentage must be between 0 and 100.",
            });
            return;
          }
        }

        if (hasInvalidRefund) {
          toast({
            variant: "destructive",
            description:
              "All refund rules must have a cancellation period selected.",
          });
          return;
        }

        if (hasDuplicateRefund) {
          toast({
            variant: "destructive",
            description:
              "Duplicate cancellation periods are not allowed in refund rules.",
          });
          return;
        }

        // Submit the form since all validations passed
        handleSubmit(onSubmit)();
      } else {
        // Move to the next step for any other steps
        setActiveStep(activeStep + 1);
      }
    }
  };

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const handlePrev = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const [formData, setFormData] =
    useState<FacilityScheduleTypes>(initialValues);

  useEffect(() => {
    const currentValues = getValues();
    setFormData((prev) => ({
      ...prev,
      ...currentValues,
    }));
  }, [activeStep]);

  console.log({ watcher });

  const sheetWidthClass =
    activeStep === 2
      ? "lg:!max-w-[1100px] xl:!max-w-[1320px]"
      : "lg:!max-w-[1000px]";

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className={cn(
          "bg-formbg w-full p-0 custom-scrollbar h-full",
          sheetWidthClass,
        )}
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-start gap-3">
                  <button type="button" onClick={handleClose}>
                    <i className="fa fa-xmark text-xl"></i>
                  </button>
                  <div>
                    <h1 className="font-medium">
                      {action == "add" ? "Create" : "Edit"} Booking Schedule
                    </h1>
                    <CompactStepIndicator
                      activeStep={activeStep}
                      steps={steps}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {activeStep !== 1 && (
                    <Button
                      className="w-[80px]"
                      variant="outline"
                      onClick={handlePrev}
                      disabled={activeStep === 1}
                    >
                      Previous
                    </Button>
                  )}
                  {activeStep !== steps.length && (
                    <Button
                      className="w-[80px]"
                      onClick={handleNext}
                      disabled={activeStep === steps.length}
                    >
                      Next
                    </Button>
                  )}
                  {activeStep === steps.length && (
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className="w-[80px]"
                    >
                      {!isSubmitting && (
                        <i className="fa fa-floppy-disk pr-2"></i>
                      )}
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  )}
                </div>
              </div>
            </SheetHeader>

            <SheetDescription className="h-full  p-6  ">
              {getStepContent(activeStep, {
                control,
                errors,
                watch,
                setValue,
                trigger,
                action,
                open: isOpen,
              })}
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default FacilityScheduleForm;
